import * as PIXI from 'pixi.js';

import { Config } from '../../../video-data/config';

import { TypedParentMixin } from '../../parentMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import { TypedSerializableMixin } from '../../serializableMixin';

import {
    AlphaAttribute,
    BooleanAttribute,
    ColorAttribute,
    FontAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
    isTemplateAttribute,
} from '../../attributes';
import { TextDirection, ValueMapMode } from '../../../enums';
import { int2rgba } from '../../../libs/videoData';
import type { RawTextAssetStyleData } from '../../../video-data/types';
import {
    getAllTemplateAttributes,
    getTemplateValueAttribute,
    isTemplateReference,
    stripTemplateReferencePrefix,
} from '../../attributes/index';
import type { CopyPasteData, CopyPasteStyle } from '../BaseAsset';
import { findTemplateIndexErrors, getStylePropertiesToCopy } from '../utils';
import type { AssetTemplateReferenceMap, Resolution } from '../../../types';
import { getGlobalFontManager } from '../../FontManager';
import type { ParentMixinSettings } from '../../parentMixin';
import type { DynamicGraphicAsset, DynamicGraphicType, GetDynamicGraphicType } from './types';
import type { TextDynamicAnimationRendererSettings } from './renderer/TextDynamicAnimationBase';
import { TextStyleRenderer } from './renderer/TextStyleRenderer';
import type { TextStyleRendererSettings } from './renderer/TextStyleRenderer';
import { TextAssetDynamicBox } from './asset/TextAssetDynamicBox';
import { TextAssetDynamicAnimation } from './asset/TextAssetDynamicAnimation';
import { TextAssetDynamicCircle } from './asset/TextAssetDynamicCircle';
import { TextAssetDynamicBoxBorder } from './asset/TextAssetDynamicBoxBorder';

type TextAssetStyleSettings = ParentMixinSettings & {
    name?: string;
    referenceToOutputMultiplier: number;
    getReferenceResolution: () => Resolution;
    getOutputResolution: () => Resolution;
    canFitGraphicsToText?: boolean;
};

export const ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA = 'animationConfigurations';

export class TextAssetStyle
    extends TypedUpdateFlowMixin(TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter)))
    implements CopyPasteStyle
{
    override _classType = 'CanvasTextAssetStyle';

    #destroyed = false;

    @FontAttribute()
    accessor fontUrl = Config.DEFAULT_FONT_URL;
    @StringAttribute({ choices: Object.values(Config.TEXT_DIRECTION), serialize: false })
    accessor textDirection: TextDirection = TextDirection.LTR;

    // FontSize is not serialized because it is a transient property that is calculated by the TextAsset
    @NumberAttribute({ serialize: false })
    accessor fontSize = 26;

    // Leading is not serialized because it is a transient property that is calculated by the TextAsset
    @NumberAttribute({ serialize: false })
    accessor leading = 0.2;
    @ColorAttribute()
    accessor fill: number = 0;
    /**
     * Never serialized - used to show/hide text programmatically in UI code.
     * @internal
     */
    @AlphaAttribute({ serialize: false })
    accessor _fillAlpha = 1;
    // Specifies if this style should render the text within the highlighted areas
    @BooleanAttribute()
    accessor renderHighlighted = false;
    // Specifies if the normal style should use the highlighted color instead of the normal color to render the highlighted areas
    @BooleanAttribute()
    accessor renderHighlightedColor = false;
    @BooleanAttribute()
    accessor shadow = false;
    @NumberAttribute({ min: 0, max: 360 })
    accessor shadowAngle = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor shadowBlur = 5;
    @AlphaAttribute()
    accessor shadowAlpha = 0.5;
    @ColorAttribute()
    accessor shadowColor: number = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor shadowDistance = 1;

    #referenceToOutputMultiplier = 1;
    #canFitGraphicsToText = false;

    #getReferenceResolution: () => Resolution;
    #getOutputResolution: () => Resolution;

    #graphics: DynamicGraphicAsset[] = [];

    #animationConfigurations: TextAssetDynamicAnimation[] = [];

    constructor(settings: TextAssetStyleSettings) {
        super(settings);

        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;
        this.#canFitGraphicsToText = settings.canFitGraphicsToText ?? false;
        this.#getReferenceResolution = settings.getReferenceResolution;
        this.#getOutputResolution = settings.getOutputResolution;
    }

    get destroyed() {
        return this.#destroyed;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.#referenceToOutputMultiplier;
    }

    get animationConfigurations() {
        return this.#animationConfigurations;
    }

    _animationConfigurationsCustomLoad(properties: Record<string, unknown>[] | undefined) {
        if (!properties) {
            return;
        }
        this.#animationConfigurations.splice(properties.length);
        properties.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    /**
     * @deprecated added for backward compatibility. Use {@link animationConfigurations} instead.
     */
    get objectAnimation() {
        return this.#animationConfigurations;
    }

    /**
     * @deprecated added for backward compatibility. Use the length of {@link animationConfigurations} instead.
     */
    get hasObjectAnimation() {
        return this.#animationConfigurations.length > 0;
    }

    /**
     * Adds a TextAssetDynamicAnimation to the `animation` list.
     * The added configurator can be configured to animate any attribute on the asset.
     */
    addAnimation() {
        const animation = new TextAssetDynamicAnimation({
            parent: this,
            getReferenceResolution: this.#getReferenceResolution,
            getOutputResolution: this.#getOutputResolution,
        });

        // do not mutate, we need to create a new reference for React
        this.#animationConfigurations = [...this.#animationConfigurations, animation];
        this.requiresUpdate = true;

        return animation;
    }

    removeAnimation(index: number) {
        // do not mutate, we need to create a new reference for React
        const animation = [...this.#animationConfigurations];
        animation.splice(index, 1);
        this.#animationConfigurations = animation;
        this.requiresUpdate = true;
    }

    get graphics(): readonly DynamicGraphicAsset[] {
        return this.#graphics;
    }

    get colorGraphics(): readonly DynamicGraphicAsset[] {
        return this.#graphics.filter((graphic) => !graphic.asMask);
    }

    get alphaGraphics(): readonly DynamicGraphicAsset[] {
        return this.#graphics.filter((graphic) => graphic.asMask);
    }

    #createGraphic<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T>;
    #createGraphic(type: DynamicGraphicType): DynamicGraphicAsset {
        let graphic: DynamicGraphicAsset;
        const settings = {
            parent: this,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            getReferenceResolution: this.#getReferenceResolution,
            getOutputResolution: this.#getOutputResolution,
            canFitGraphicsToText: this.#canFitGraphicsToText,
        };
        switch (type) {
            case Config.BOX_BORDER_GRAPHIC:
                graphic = new TextAssetDynamicBoxBorder(settings);
                break;
            case Config.BOX_GRAPHIC:
                graphic = new TextAssetDynamicBox(settings);
                break;
            case Config.CIRCLE_GRAPHIC:
                graphic = new TextAssetDynamicCircle(settings);
                break;
            default:
                throw new Error(`Invalid type ${type} for graphic`);
        }
        return graphic;
    }

    _graphicsCustomLoad(
        properties: RawTextAssetStyleData[],
        settings = { skipTemplateAttributes: false },
    ) {
        this.#graphics.splice(properties.length);
        properties.forEach((dataObj, index) => {
            const graphic = this.#graphics[index] ?? this.addGraphicByType(dataObj.type);
            graphic.load(dataObj, settings);
        });
    }

    addGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.#createGraphic(type);
        // do not mutate, we need to create a new reference for React
        this.#graphics = [...this.#graphics, graphic];
        this.requiresUpdate = true;

        return graphic;
    }

    addColorGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.addGraphicByType(type);
        graphic.asMask = false;
        return graphic;
    }

    addAlphaGraphicByType<T extends DynamicGraphicType>(type: T): GetDynamicGraphicType<T> {
        const graphic = this.addGraphicByType(type);
        graphic.asMask = true;
        return graphic;
    }

    removeGraphic(index: number) {
        // do not mutate, we need to create a new reference for React
        const graphics = [...this.#graphics];
        graphics.splice(index, 1);
        this.#graphics = graphics;
        this.requiresUpdate = true;
    }

    getRendererSettings(
        settings = {
            leading: this.leading,
            fontSize: this.fontSize,
        },
    ): TextStyleRendererSettings {
        // convert asset color to canvas color
        const fill = int2rgba(this.fill as number, this._fillAlpha);
        const shadowColor = int2rgba(this.shadowColor, this.shadowAlpha);

        // convert asset graphics to canvas graphics
        const graphics = this.#graphics
            .filter((graphic) => graphic.enabled)
            .map((graphic) => graphic.getCanvasRendererSettings());

        // convert asset animations to canvas animations
        const animationConfigurations = this.#animationConfigurations
            .filter((animationConfiguration) => animationConfiguration.enabled)
            .map(
                (animationConfiguration) =>
                    animationConfiguration.getCanvasRendererSettings() as TextDynamicAnimationRendererSettings,
            );

        const fontManager = getGlobalFontManager();
        const fontLoader = fontManager.getFontBySourceIfLoaded(this.fontUrl);
        // Font loader can be null while loading font, use a default, as a family name is required in the bridge data.
        const fontFamily = fontLoader?.familyName ?? 'Arial';

        return {
            fill,
            fontFamily,
            fontSize: settings.fontSize * this.referenceToOutputMultiplier,
            lineHeight: 1.0 + settings.leading / settings.fontSize,
            textDirection: this.textDirection,
            renderHighlighted: this.renderHighlighted,
            renderHighlightedColor: this.renderHighlightedColor,
            shadow: this.shadow,
            shadowAngle: this.shadowAngle,
            shadowBlur: this.shadowBlur * this.referenceToOutputMultiplier,
            shadowColor: shadowColor,
            shadowDistance: this.shadowDistance * this.referenceToOutputMultiplier,
            graphics,
            animationConfigurations,
        };
    }

    get stylePropertiesToCopy(): string[] {
        return [
            'fontUrl',
            'fill',
            'renderHighlighted',
            'renderHighlightedColor',
            'shadow',
            'shadowAngle',
            'shadowBlur',
            'shadowAlpha',
            'shadowColor',
            'shadowDistance',
            'animationConfigurations',
            'graphics',
        ];
    }

    copyStyleProperties(): CopyPasteData {
        return getStylePropertiesToCopy(this);
    }

    pasteStyleProperties(settings: CopyPasteData): void {
        const templateIndexErrorsList: string[] = [];

        findTemplateIndexErrors(
            this.template,
            '',
            settings._templateReferenceMap,
            templateIndexErrorsList,
        );

        if (templateIndexErrorsList.length > 0) {
            throw new Error(templateIndexErrorsList.join(' '));
        }

        // Only get whitelisted properties
        const filteredSettings = Object.fromEntries(
            Object.entries(settings).filter(([key, _value]) => {
                if (!this.stylePropertiesToCopy.includes(key)) {
                    return false;
                }
                return true;
            }),
        );

        this.load(filteredSettings);
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this).concat([
            ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA,
            'graphics',
        ]);
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'fontUrl',
            'textDirection',
            'fill',
            'shadowColor',
            'graphics',
        ]);
    }

    get animatableProperties(): readonly string[] {
        return TextStyleRenderer.animatableProperties;
    }

    /** The list of geometry properties that can be animated during draw. */
    get animatableGeometricProperties(): readonly string[] {
        return TextStyleRenderer.animatableGeometricProperties;
    }

    /**
     * Builds a map of all values referencing template value in the style
     */
    buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        // GET_REFERENCE_VALUES mode always returns string values
        return this._buildValueMap(ValueMapMode.GET_REFERENCE_VALUES) as AssetTemplateReferenceMap;
    }

    /**
     * Builds value map of all attributes in style and its graphics.
     * Prefix can be used to prepend a prefix to all paths.
     * Ex.: for normalStyle, it's useful to prepend "normalStyle" to all paths.
     * @internal
     */
    _buildValueMap(
        valueMapMode = ValueMapMode.GET_REFERENCE_VALUES,
        prefix: string | null = null,
        onlyVisible = false,
    ): Record<string, unknown> {
        const result: Record<string, unknown> = {};

        // Add `.` after prefix, if present.
        if (prefix) {
            prefix = `${prefix}.`;
        } else {
            prefix = '';
        }

        const attributes = getAllTemplateAttributes(this, valueMapMode);

        // Cast this to allow dynamic property access
        const self = this as unknown as Record<string, unknown>;
        attributes.forEach((attr) => {
            const attrKey = `${prefix}${attr}`;
            const templateValue = getTemplateValueAttribute(this, attr);

            if (isTemplateReference(templateValue)) {
                if (valueMapMode === ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
                    result[attrKey] = self[attr];
                } else {
                    result[attrKey] = stripTemplateReferencePrefix(templateValue);
                }
            } else if (valueMapMode !== ValueMapMode.GET_REFERENCE_VALUES) {
                result[attrKey] = self[attr];
            }
        });

        this.graphics.forEach((graphic, index) => {
            if (graphic.asMask) {
                // skip alpha graphics because they're not visible
                return;
            }
            const attrKeyPrefix = `${prefix}graphics.${index}.`;
            // Cast graphic to allow dynamic property access
            const graphicRecord = graphic as unknown as Record<string, unknown>;
            // TODO: we should use getTemplateAttributes here once we have a way to get inherited attributes
            const templateAttributes = graphic.propertiesToSerialize.filter((attr) =>
                isTemplateAttribute(graphic, attr as Extract<keyof typeof graphic, string>),
            );
            templateAttributes.forEach((attr) => {
                const attrKey = `${attrKeyPrefix}${attr}`;
                const templateValue = getTemplateValueAttribute(graphic, attr);
                if (isTemplateReference(templateValue)) {
                    if (valueMapMode === ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE) {
                        result[attrKey] = graphicRecord[attr];
                    } else {
                        result[attrKey] = stripTemplateReferencePrefix(templateValue);
                    }
                } else if (valueMapMode !== ValueMapMode.GET_REFERENCE_VALUES) {
                    if (
                        !(
                            onlyVisible &&
                            attr === 'strokeColor' &&
                            graphicRecord['strokeWidth'] === 0
                        )
                    ) {
                        result[attrKey] = graphicRecord[attr];
                    }
                }
            });
        });
        return result;
    }

    removeDynamicStyles() {
        this.#animationConfigurations = [];
        this.#graphics = [];
        this.requiresUpdate = true;
    }

    override get readonlyProperties(): string[] {
        return [];
    }

    /**
     * @internal
     */
    get textColor() {
        return this.fill;
    }

    /**
     * @deprecated, use {@link fill} instead
     * @internal
     */
    set textColor(value) {
        this.fill = value;
    }

    override loadEditable(dataObj: Record<string, unknown> & { graphics?: unknown[] }) {
        if ('graphics' in dataObj && dataObj.graphics) {
            dataObj.graphics = dataObj.graphics.slice(0, this.graphics.length);
        }
        super.loadEditable(dataObj);
        return this;
    }
}
